import React, { useEffect, useMemo, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../utils/api';
import { useLocation, useNavigate } from 'react-router-dom';

const resolveSourceLabel = (type) => {
  if (type === 'admin') return 'Admin';
  if (type === 'public') return 'Employee';
  return '—';
};

const normalizeSearchValue = (value) => String(value ?? '').toLowerCase();

const PendingEmployeeApprovals = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageStatus, setPageStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('admin');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRequests = useMemo(() => {
    const trimmedSearch = searchTerm.trim().toLowerCase();
    if (!trimmedSearch) {
      return requests;
    }

    return requests.filter((request, index) => {
      const visibleValues = [
        index + 1,
        request.personal?.name,
        request.personal?.phone || request.phone,
        request.personal?.tempId || request.employee?.tempId,
        request.personal?.permId || request.employee?.permId,
        request.outlet?.name,
        request.personal?.aadhar || request.aadhar,
        resolveSourceLabel(request.requestedBy?.type),
        'Pending',
      ];

      return visibleValues.some((value) => normalizeSearchValue(value).includes(trimmedSearch));
    });
  }, [requests, searchTerm]);

  const pendingCount = useMemo(() => requests.length, [requests]);
  const filteredPendingCount = useMemo(() => filteredRequests.length, [filteredRequests]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const requestedByType = activeTab === 'employee' ? 'public' : 'admin';
      const response = await apiRequest(
        `/api/admin/employee-approvals?status=pending&requestedByType=${requestedByType}`,
        {},
        navigate,
      );
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Failed to load pending approvals');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchTerm('');
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (location.state?.status) {
      setPageStatus(location.state.status);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  return (
    <DashboardLayout title="Pending Employee Approvals">
      {pageStatus && (
        <div
          className={`alert alert-${pageStatus.type === 'warning' ? 'warning' : 'success'} d-flex justify-content-between align-items-start`}
          role="alert"
        >
          <div>{pageStatus.message}</div>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setPageStatus(null)} />
        </div>
      )}

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">Pending Approvals</h5>
              <p className="text-muted mb-0">Review and action on submissions awaiting approval.</p>
            </div>
            <span className="badge bg-danger-subtle text-danger">
              {searchTerm.trim()
                ? `${filteredPendingCount} of ${pendingCount} awaiting action`
                : `${pendingCount} awaiting action`}
            </span>
          </div>

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'admin' ? 'active' : ''}`}
                onClick={() => setActiveTab('admin')}
              >
                Admin Requests
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'employee' ? 'active' : ''}`}
                onClick={() => setActiveTab('employee')}
              >
                Employee Requests
              </button>
            </li>
          </ul>

          <div className="mb-3">
            <label htmlFor="approvalSearch" className="form-label">Search approvals</label>
            <input
              id="approvalSearch"
              type="search"
              className="form-control"
              placeholder="Search by name, mobile, ID, outlet, Aadhaar, source, or status"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              disabled={loading || requests.length === 0}
            />
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : requests.length === 0 ? (
            <p className="text-muted mb-0">
              {activeTab === 'admin'
                ? 'No admin-submitted approval requests right now.'
                : 'No employee-submitted approval requests right now.'}
            </p>
          ) : filteredRequests.length === 0 ? (
            <p className="text-muted mb-0">No matching approval requests found.</p>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr>
                    <th scope="col">S.No</th>
                    <th scope="col">Employee Name</th>
                    <th scope="col">Mobile Number</th>
                    <th scope="col">Temp ID</th>
                    <th scope="col">Permanent ID</th>
                    <th scope="col">Outlet Name</th>
                    <th scope="col">Aadhaar Number</th>
                    <th scope="col">Request Source</th>
                    <th scope="col">Approval</th>
                    <th scope="col" className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.map((request, index) => (
                    <tr key={request.id}>
                      <td>{index + 1}</td>
                      <td>{request.personal?.name || '—'}</td>
                      <td>{request.personal?.phone || request.phone || '—'}</td>
                      <td>
                        {request.personal?.tempId || request.employee?.tempId ? (
                          <span className="badge bg-info-subtle text-info-emphasis">
                            {request.personal?.tempId || request.employee?.tempId}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {request.personal?.permId || request.employee?.permId ? (
                          <span className="badge bg-success-subtle text-success-emphasis">
                            {request.personal?.permId || request.employee?.permId}
                          </span>
                        ) : '—'}
                      </td>
                      <td>{request.outlet?.name || '—'}</td>
                      <td>{request.personal?.aadhar || request.aadhar || '—'}</td>
                      <td>{resolveSourceLabel(request.requestedBy?.type)}</td>
                      <td>
                        <span className="badge bg-warning text-dark">Pending</span>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-link text-danger"
                          onClick={() => navigate(`/dashboard/pending-approvals/${request.id}`)}
                          aria-label={`View ${request.personal?.name || 'employee'} details`}
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PendingEmployeeApprovals;
